import { ChevronDown, Loader2 } from 'lucide-react'
import { ChainIcon } from '@/components/ChainIcon'
import { TokenIcon } from '@/components/icons/TokenIcons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useUI } from '@/hooks/useUI'
import { FONT_BODY } from '@/config/theme'
import { CHAIN_REGISTRY } from '@/config/chains'
import type { Chain } from '@/types'

interface TokenPillProps {
  token: string
  chain: Chain | null
  onTokenClick: () => void
  onChainClick: () => void
  disabled?: boolean
  /**
   * #301a RE-ENTRY 3 — the CHAIN button alone is unavailable because the live route table has not
   * arrived yet, so a network pick made right now would be decided with no routing evidence at all.
   *
   * Distinct from `disabled`, which is the TRANSFER state and greys the WHOLE pill (token button
   * included) via `pointerEvents:'none'`. This gates one button, with a loading affordance on it —
   * the operator's explicit call is that it must read "loading", never "broken".
   */
  chainPending?: boolean
}

// Map token symbols to basic color data for TokenIcon fallback
const TOKEN_COLORS: Record<string, string> = {
  USDC: '#2775CA', USDT: '#26A17B', ETH: '#627EEA',
  SOL: '#9945FF', BNB: '#F0B90B', MATIC: '#8247E5',
}

export function TokenPill({ token, chain, onTokenClick, onChainClick, disabled, chainPending }: TokenPillProps) {
  const c = useThemeColors()
  const UI = useUI()

  const tokenObj = { symbol: token, color: TOKEN_COLORS[token] || '#888' }

  // #254 — network-first display: a NATIVE selection (token === the chain's native currency)
  // is labelled by its network instead of the bare symbol, matching how POL/SOL/TRX already read
  // as their chain. Display-ONLY — `token` (the canonical symbol) is unchanged, so
  // routing/balances/quotes are untouched. Stablecoins keep the symbol.
  //
  // #301 item 1 REFINES #254 — it does NOT revert it. #254's invariant is "a native never shows a
  // bare symbol belonging to another chain"; only the FORMAT changes here, from the full `name`
  // ("Base") to the 4-char `short` ("BASE"), and ETH-on-Base still never reads "ETH". The chain's
  // own brand mark replaces the ETH token glyph for the same reason: a native on Base/Robinhood was
  // rendering the Ethereum glyph in Ethereum blue, because TOKEN_COLORS is keyed on the SYMBOL.
  //
  // REVIEWER NOTE — not a regression: `ethereum.short` IS 'ETH' (config/chains.ts:16), so the
  // ETH-on-Ethereum pill legitimately reads "ETH". That is the chain CODE coinciding with the
  // symbol, not #254's defect — #254's target was ETH-on-**Base** reading "ETH", which this still
  // makes impossible. Do not "fix" it by special-casing ethereum.
  //
  // ACCEPTED consequence (operator's call, #301 GATE 1): a native now reads e.g. "BASE" in the token
  // button next to "BASE" in the adjacent chain button (`:short` there too) — identical where they
  // were merely similar. The two buttons do different things and stripping the chain button's label
  // leaves a bare chevron that reads as broken, so NEITHER label is suppressed.
  const isNativeSelection = chain != null && CHAIN_REGISTRY[chain].nativeCurrency === token
  const tokenLabel = isNativeSelection ? CHAIN_REGISTRY[chain].short : token
  // The ARIA label keeps the FULL name. If it followed the visible short, a screen reader would
  // announce "currently B-A-S-E" (or spell "R-H"), which is strictly worse than what #254 shipped.
  // Visible = short, announced = name.
  const tokenAriaLabel = isNativeSelection ? CHAIN_REGISTRY[chain].name : token

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto',
    }}>
      <button
        onClick={onTokenClick}
        aria-label={`Change token, currently ${tokenAriaLabel}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: c.iB, border: `1px solid ${c.dv}`, borderRadius: 14,
          padding: UI.tokPad, cursor: 'pointer', fontFamily: FONT_BODY,
          transition: 'border-color 0.15s',
        }}
      >
        {/* Native → the CHAIN's own brand mark (ChainIcon: brand SVG, else a letter circle on
            CHAIN_REGISTRY.color/.letter). Stablecoin → its own token glyph, untouched. */}
        {isNativeSelection
          ? <ChainIcon chain={chain} size={UI.tokIcon} />
          : <TokenIcon token={tokenObj} size={UI.tokIcon} />}
        <span style={{ fontSize: UI.tokFont, fontWeight: 600, color: c.tx, whiteSpace: 'nowrap' }}>{tokenLabel}</span>
        <ChevronDown size={14} style={{ color: c.mu }} aria-hidden="true" />
      </button>
      {/* #301a RE-ENTRY 3 — the routes-pending chain button.
          MIRRORS Job B's `Balance: —` pending row (SwapCard/index.tsx `balance-pending`): exactly ONE
          slot is replaced by a dedicated pending element carrying a `*-pending` testid, every sibling
          keeps its structure, and the control stays in the layout rather than disappearing.
          DELIBERATE DIVERGENCE from a literal em dash, in the same spirit as that row's own
          three-spans-vs-two note: the slot Job B blanks is the one whose CONTENT is unknowable. Here
          the chain IS known (it comes from localStorage or the default) — what is unknowable is
          whether opening the picker is safe yet — so the pending element replaces the CHEVRON, the
          affordance that promises "this opens", and not the chain's own label. A spinning indicator
          rather than a static dash is the operator's explicit requirement: it must read "loading",
          not "broken". `spin` is the app-wide keyframe (styles/globals.css:220), already used inline
          the same way by StepRow, and the global prefers-reduced-motion block (:311) neutralises it. */}
      <button
        onClick={onChainClick}
        disabled={chainPending}
        aria-busy={chainPending || undefined}
        aria-label={
          chainPending ? 'Loading networks'
          : chain ? `Change network, currently ${CHAIN_REGISTRY[chain].name}`
          : 'Select network'
        }
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: 'none', border: `1px solid ${c.dv}`, borderRadius: 10,
          padding: UI.chainPad, cursor: chainPending ? 'progress' : 'pointer',
          fontFamily: FONT_BODY, fontSize: UI.chainFont,
          color: c.ts, transition: 'border-color 0.15s',
          opacity: chainPending ? 0.65 : 1,
        }}
      >
        {chain && <ChainIcon chain={chain} size={UI.chainIcon} />}
        <span>{chain ? CHAIN_REGISTRY[chain].short : 'Select'}</span>
        {chainPending
          ? (
            <span data-testid="chain-pending" aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Loader2 size={12} style={{ color: c.mu, animation: 'spin 0.9s linear infinite' }} />
            </span>
          )
          : <ChevronDown size={12} style={{ color: c.mu }} aria-hidden="true" />}
      </button>
    </div>
  )
}
